import { ENTITY_COLORS, timeAgo } from '../utils/map-constants.js'
import { hasIcon } from '../utils/icon-registry.js'
import { resolveIncidentGroup } from './incident-category-resolver.js'
import { resolveDeviceType, deviceTypeLabel } from './device-type-resolver.js'
import { detectPartnerSystem } from './partner-system.js'
import { externalDeviceStatusLabel } from './external-device-status.js'

// Normalize status values from Kalan API to our internal 'online'/'offline'/'alert'
// Each entity type uses different status strings:
//   devices: 'online' / 'down'
//   buses: 'up' / 'down' / 'alert'
//   cameras: 'up' or 'online' / 'down'
//   lpr: 'active' / 'partial' / 'inactive'
//   sensors: 'up' / 'down' / 'alert' / 'reported'
function isOnline(status) {
  return status === 'online' || status === 'up' || status === 'active' || status === 'partial'
}

function isAlert(status) {
  return status === 'alert' || status === 'reported'
}

export const ENTITY_CONFIGS = {
  device: {
    entityType: 'device',
    hasConnectionStatus: true,
    get color() { return ENTITY_COLORS.device },
    isStatic: false,
    label: 'Unidades',
    mdiIcon: 'mdi-car-emergency',
    getCoords(item) {
      const pos = item.position
      if (!pos || !pos.longitude || !pos.latitude) return null
      return [pos.longitude, pos.latitude]
    },
    getTitle(item) { return item.name || item.uniqueId || 'Device ' + item.id },
    getSubtitle(item) {
      // Tipo legible (Ambulancia / Patrulla Municipal / Tránsito / etc.)
      // primero — es la informacion mas util para que el operador clasifique
      // el recurso de un vistazo en tooltips, sidebar y header del drawer.
      // Status y velocidad despues, separados por "·".
      const slug = resolveDeviceType(item)
      const typeLabel = deviceTypeLabel(slug)
      const speed = item.position?.speed
      const status = isOnline(item.status) ? 'En línea' : 'Fuera de línea'
      const parts = [typeLabel, status]
      if (speed > 0) parts.push(Math.round(speed) + ' km/h')
      return parts.join(' · ')
    },
    // 4 buckets: online, offline, unknown, down. Antes colapsaba a 2 →
    // el marker no distinguía equipos caídos de sin comunicación.
    // AJUSTE-PALETA-MARKERS §4.1.
    getStatus(item) {
      if (item.status === 'online' || item.status === 'up') return 'online'
      if (item.status === 'unknown') return 'unknown'
      if (item.status === 'down') return 'down'
      return 'offline'
    },
    getIcon(item) {
      // Sistema MapV2: resolver canónico → slug + ícono curado por
      // tema en theming/generated/<tema>/device_<slug>_<mapMode>_<status>.svg.
      // El layer-manager arma el nombre completo via buildIconExpression
      // (concat icon + mapMode + iconStatus). Verifico que exista al
      // menos la variante day_online (si está, las demás también porque
      // las genera todas el mismo run del generator).
      var slug = resolveDeviceType(item)
      var generatedBase = 'device_' + slug
      if (hasIcon(generatedBase + '_day_online') || hasIcon(generatedBase + '_night_online')) {
        return generatedBase
      }
      // Fallback al pack legacy mientras se generan los SVGs nuevos.
      var icon = item.categoryData?.attributes?.icon
      if (!icon && item.category && !/^[0-9a-f]{24}$/i.test(item.category)) {
        icon = item.category
      }
      return icon || 'patrulla_municipal'
    },
  },

  bus: {
    entityType: 'bus',
    hasConnectionStatus: true,
    get color() { return ENTITY_COLORS.bus },
    isStatic: false,
    label: 'Transporte',
    mdiIcon: 'mdi-bus',
    getCoords(item) {
      // Buses use top-level lat/lon (like cameras), NOT nested position
      if (!item.longitude || !item.latitude) return null
      return [item.longitude, item.latitude]
    },
    getTitle(item) { return item.name || 'Bus ' + item.id },
    getSubtitle(item) {
      const speed = item.speed || item.position?.speed
      const status = isOnline(item.status) ? 'En línea' : isAlert(item.status) ? 'Alerta' : 'Fuera de línea'
      return speed > 0 ? status + ' · ' + Math.round(speed) + ' km/h' : status
    },
    getStatus(item) { return isAlert(item.status) ? 'alert' : isOnline(item.status) ? 'online' : 'offline' },
    getIcon(item) { return item.categoryData?.attributes?.icon || 'bus' },
  },

  camera: {
    entityType: 'camera',
    hasConnectionStatus: true,
    get color() { return ENTITY_COLORS.camera },
    isStatic: true,
    label: 'Cámaras',
    mdiIcon: 'mdi-cctv',
    getCoords(item) {
      if (!item.longitude || !item.latitude) return null
      return [item.longitude, item.latitude]
    },
    getTitle(item) { return item.name || 'Cámara ' + item.id },
    getSubtitle(item) { return isOnline(item.status) ? 'En línea' : 'Fuera de línea' },
    getStatus(item) { return isOnline(item.status) ? 'online' : 'offline' },
    getIcon() { return 'camera' },
  },

  lpr: {
    entityType: 'lpr',
    hasConnectionStatus: true,
    get color() { return ENTITY_COLORS.lpr },
    isStatic: true,
    label: 'LPR',
    mdiIcon: 'mdi-camera-front',
    getCoords(item) {
      if (!item.longitude || !item.latitude) return null
      return [item.longitude, item.latitude]
    },
    getTitle(item) { return item.name || 'LPR ' + item.id },
    // Texto del marker en el mapa: el identificador corto (MXBUS004) en vez
    // del nombre largo. Sin identifier cae al title.
    getLabel(item) { return item.identifier || item.name || 'LPR ' + item.id },
    getSubtitle(item) {
      return item.status === 'active' ? 'Activo' : item.status === 'partial' ? 'Parcial' : 'Inactivo'
    },
    // 3 buckets normalizados a naming consistente con el resto de capas
    // (online/offline + partial). Permite que el filter default del store
    // (['online']) siga matchando LPR completamente operativos, y la
    // extensión 'partial' se distingue para el modo degradado.
    // AJUSTE-PALETA-MARKERS §4.1.
    getStatus(item) {
      if (item.status === 'active')  return 'online'
      if (item.status === 'partial') return 'partial'
      return 'offline'
    },
    getIcon() { return 'lpr' },
  },

  incident: {
    entityType: 'incident',
    get color() { return ENTITY_COLORS.incident },
    isStatic: false,
    label: 'Incidentes',
    mdiIcon: 'mdi-shield-alert',
    getCoords(item) {
      if (!item.longitude || !item.latitude) return null
      return [item.longitude, item.latitude]
    },
    getTitle(item) { return item.categoryData?.name || item.type || 'Incidente ' + item.id },
    getSubtitle(item) {
      const status = item.status === 'ACTIVO' ? 'Activo' : 'Histórico'
      return status + ' · ' + timeAgo(item.createdAt || item.serverTime)
    },
    getStatus(item) { return item.status === 'ACTIVO' ? 'alert' : 'idle' },
    getIcon(item) {
      // El raw llega con shape variable: algunos incidentes traen
      // item.category como string de grupo ('seguridad', 'medico'),
      // otros solo traen item.type (ObjectId) + item.categoryData del
      // subtipo específico del catálogo CNIE. El resolver unifica
      // ambos casos y hace inferencia por nombre como último recurso
      // para garantizar diferenciación visual en el mapa.
      var slug = resolveIncidentGroup(item)
      var base = 'incident_' + slug
      // Los SVG tienen sufijos variables según el pack y el flag del
      // sistema de temas: legacy usa `_up`/`_day_up`, el sistema
      // declarativo usa `_alta`/`_day_alta` (y otras prioridades).
      // Verificamos cualquier variante conocida para que el fallback
      // solo dispare cuando la categoría realmente no existe.
      var variantes = ['_up', '_day_up', '_alta', '_day_alta', '_media', '_day_media']
      for (var i = 0; i < variantes.length; i++) {
        if (hasIcon(base + variantes[i])) return base
      }
      return 'incident_seguridad'
    },
    // Cuando el incident fue enriquecido por el WS de tripData con
    // positions del reportante (rapidSosLocations) o lleva systemName
    // adjunto (Map.vue legacy lo hace en setAdditionalData), exponer
    // el proveedor para que pinte el badge sobre el marker. Si el
    // incident no tiene interconexión, el helper retorna null y el
    // layer del badge filtra la feature. NO pasamos 'reportant' como
    // entityType — un incident sin enriquecer NO debe pintar badge.
    getPartner(item) { return detectPartnerSystem(item, 'incident') },
  },

  sensor: {
    entityType: 'sensor',
    hasConnectionStatus: true,
    get color() { return ENTITY_COLORS.sensor },
    isStatic: true,
    label: 'Botones de Pánico',
    mdiIcon: 'mdi-alarm-light',
    getCoords(item) {
      if (!item.longitude || !item.latitude) return null
      return [item.longitude, item.latitude]
    },
    getTitle(item) { return item.name || 'Sensor ' + item.id },
    getSubtitle(item) {
      return item.status === 'alert' ? 'Alerta' : item.status === 'reported' ? 'Reportado' : 'Normal'
    },
    // 4 buckets: online (armado normal), alert (alerta activa), reported
    // (atendido pendiente de cierre), offline (sin comunicación). Separar
    // `reported` de `alert` permite que el marker no reclame atención
    // urgente una vez que la alerta fue atendida (AJUSTE-PALETA-MARKERS §4.1).
    getStatus(item) {
      if (item.status === 'alert') return 'alert'
      if (item.status === 'reported') return 'reported'
      if (isOnline(item.status)) return 'online'
      return 'offline'
    },
    getIcon(item) { return item.category?.attributes?.icon || 'sensor' },
  },

  external_sensor: {
    entityType: 'external_sensor',
    get color() { return ENTITY_COLORS.external_sensor },
    isStatic: true,
    label: 'Sensores Externos',
    mdiIcon: 'mdi-access-point-network',
    getCoords(item) {
      if (!item.longitude || !item.latitude) return null
      return [item.longitude, item.latitude]
    },
    getTitle(item) { return item.name || 'Sensor Ext. ' + (item.id || item._id) },
    getSubtitle(item) { return item.systemName || '' },
    getStatus() { return 'online' },
    getIcon() { return 'external_sensor' },
  },

  external_sensor_route: {
    entityType: 'external_sensor_route',
    get color() { return ENTITY_COLORS.external_sensor_route },
    isStatic: true,
    label: 'Rutas Sensor',
    mdiIcon: 'mdi-map-marker-path',
    getCoords(item) {
      if (!item.longitude || !item.latitude) return null
      return [item.longitude, item.latitude]
    },
    getTitle(item) { return item.name || 'Ruta ' + (item.id || item._id) },
    getSubtitle(item) { return item.systemName || '' },
    getStatus() { return 'online' },
    getIcon() { return 'external_sensor_route' },
  },

  // Unidades de terceros (corporaciones, dependencias, flotas privadas) que
  // reportan por protocolo OsmAnd directo a apiv2. No viven en el Traccar
  // legacy, por eso no son `device` ni `bus`. Ver el spec del módulo
  // external-devices en apiv2-edomex.
  external_device: {
    entityType: 'external_device',
    hasConnectionStatus: true,
    get color() { return ENTITY_COLORS.external_device },
    isStatic: false,
    label: 'Unidades externas',
    mdiIcon: 'mdi-car-connected',
    getCoords(item) {
      // Nivel raíz, como bus — NO item.position (ese es el shape de device).
      if (item.longitude == null || item.latitude == null) return null
      return [item.longitude, item.latitude]
    },
    getTitle(item) {
      return item.name || item.uniqueId || 'Unidad externa ' + (item.id || item._id)
    },
    getSubtitle(item) {
      // La organización va primero: es lo que le dice al operador de quién
      // es la unidad, que es la razón de que esta capa exista.
      var estado = externalDeviceStatusLabel(item.status)
      var parts = []
      if (item.organization) parts.push(item.organization)
      parts.push(estado)
      if (item.speed > 0) parts.push(Math.round(item.speed) + ' km/h')
      return parts.join(' · ')
    },
    // 4 buckets. `reported` separado de `alert` para que el marker deje de
    // reclamar urgencia una vez que el operador levantó el folio CAD —
    // mismo criterio que el sensor (AJUSTE-PALETA-MARKERS §4.1).
    getStatus(item) {
      if (item.status === 'alert') return 'alert'
      if (item.status === 'reported') return 'reported'
      if (item.status === 'up') return 'online'
      return 'offline'
    },
    getIcon() { return 'external_device' },
  },

  presa: {
    entityType: 'presa',
    get color() { return ENTITY_COLORS.presa },
    isStatic: true,
    label: 'Presas',
    mdiIcon: 'mdi-waves',
    getCoords(item) {
      if (!item.longitude || !item.latitude) return null
      return [item.longitude, item.latitude]
    },
    getTitle(item) { return item.name || item.nombre || 'Presa ' + (item.id || item._id) },
    getSubtitle(item) { return item.estado ? 'Nivel: ' + item.estado : '' },
    getStatus() { return 'online' },
    getIcon() { return 'presa' },
  },

  sismo: {
    entityType: 'sismo',
    get color() { return ENTITY_COLORS.sismo },
    isStatic: true,
    label: 'Sismos',
    mdiIcon: 'mdi-pulse',
    // El backend emite sismos con campos en español (latitud/longitud,
    // magnitud). Aceptamos ambas convenciones por si el legacy se migra.
    getCoords(item) {
      var lng = item.longitude != null ? item.longitude : item.longitud
      var lat = item.latitude != null ? item.latitude : item.latitud
      if (lng == null || lat == null) return null
      return [Number(lng), Number(lat)]
    },
    getTitle(item) {
      var mag = item.magnitud != null ? item.magnitud : item.magnitude
      // Si hay fecha + hora los usamos; si no, caemos al nombre o al ID.
      if (item.fecha || item.hora) {
        var stamp = [item.fecha, item.hora].filter(Boolean).join(' ')
        return 'Sismo ' + stamp + (mag != null ? ' · M' + mag : '')
      }
      return item.name || ('Sismo' + (mag != null ? ' M' + mag : ''))
    },
    getSubtitle(item) { return item.lugar || item.location || '' },
    getStatus() { return 'alert' },
    getIcon() { return 'sismo' },
  },

  fire: {
    entityType: 'fire',
    get color() { return ENTITY_COLORS.fire },
    isStatic: true,
    label: 'Incendios',
    mdiIcon: 'mdi-fire',
    getCoords(item) {
      if (!item.longitude || !item.latitude) return null
      return [item.longitude, item.latitude]
    },
    getTitle(item) { return item.name || 'Incendio ' + (item.id || item._id) },
    getSubtitle(item) { return item.estado || item.status || '' },
    getStatus() { return 'alert' },
    getIcon() { return 'fire' },
  },

  cpr: {
    entityType: 'cpr',
    get color() { return ENTITY_COLORS.cpr },
    isStatic: true,
    label: 'CPRs',
    mdiIcon: 'mdi-hospital-building',
    getCoords(item) {
      if (!item.longitude || !item.latitude) return null
      return [item.longitude, item.latitude]
    },
    getTitle(item) { return item.name || 'CPR ' + item.id },
    getSubtitle(item) { return item.address || '' },
    getStatus() { return 'online' },
    getIcon() { return 'cpr' },
  },

  // Persona con medida cautelar (brazalete GPS por orden judicial).
  // NO se carga masivamente: el mapa principal solo muestra los
  // alertamientos del día, y el monitor se crea al vuelo cuando el operador
  // decide seguir a una persona desde una alerta (_mcSeguirPersona).
  medida_cautelar: {
    entityType: 'medida_cautelar',
    get color() { return ENTITY_COLORS.medida_cautelar },
    isStatic: false,
    label: 'Medidas cautelares',
    mdiIcon: 'mdi-account-lock',
    getId(item) { return item.personaId || item.id || item._id },
    getCoords(item) {
      if (Array.isArray(item.coordinates) && item.coordinates.length >= 2) {
        return [item.coordinates[0], item.coordinates[1]]
      }
      var last = item.lastPosition
      if (last && last.longitude != null && last.latitude != null) {
        return [last.longitude, last.latitude]
      }
      return null
    },
    getTitle(item) { return item.personaNombre || 'Persona con medida cautelar' },
    getSubtitle(item) {
      var parts = ['Medida cautelar']
      if (typeof item.battery === 'number') parts.push('Batería ' + Math.round(item.battery) + '%')
      return parts.join(' · ')
    },
    getStatus() { return 'alert' },
    // Reusa el ícono de reportante (persona rastreada en vivo): ya existe en
    // los 4 temas del generator. Un ícono propio de brazalete es una mejora
    // pendiente del pipeline de theming.
    getIcon() { return 'reportant' },
  },

  reportant: {
    entityType: 'reportant',
    get color() { return ENTITY_COLORS.reportant },
    isStatic: false,
    label: 'Reportantes',
    mdiIcon: 'mdi-account-alert',
    // Los reportantes llegan por el WS /api/incidents/locations/socket.io
    // (subscribesystem('RAPID-SOS')) con shape:
    //   { additionalData: { phone, systemName }, positions: [...], lastPosition: {...} }
    // El telefono es la unica clave estable — no hay id Mongo ni uniqueId.
    getId(item) {
      return item.additionalData?.phone || item.callbackNumber || item.phone || item.id || item._id
    },
    getCoords(item) {
      var last = item.lastPosition || (item.positions && item.positions[item.positions.length - 1])
      if (last && last.longitude != null && last.latitude != null) {
        return [last.longitude, last.latitude]
      }
      if (item.longitude != null && item.latitude != null) {
        return [item.longitude, item.latitude]
      }
      return null
    },
    getTitle(item) {
      return item.additionalData?.phone || item.callbackNumber || item.phone || item.name || 'Reportante'
    },
    getSubtitle(item) {
      var addr = item.positions?.[0]?.additionalData?.address
      if (addr && typeof addr === 'object') {
        var parts = [addr.colonia, addr.poblacion, addr.municipio].filter(Boolean)
        if (parts.length) return parts.join(', ')
      }
      return item.additionalData?.systemName || item.systemName || 'RAPID-SOS'
    },
    getStatus() { return 'alert' },
    getIcon() { return 'reportant' },
    // Sistema de interconexión externa que originó la llamada
    // (carbyne/didi/uber/rapidsos). Pinta el badge sobre el marker y
    // alimenta el bloque "Fuente" del drawer. Pasamos entityType para
    // que el detector defaultee a 'rapidsos' cuando el payload no
    // tenga señales explícitas — todo reportante viene de esa red.
    getPartner(item) { return detectPartnerSystem(item, 'reportant') },
  },

  sos_call: {
    entityType: 'sos_call',
    get color() { return ENTITY_COLORS.sos_call },
    isStatic: false,
    label: 'SOS',
    mdiIcon: 'mdi-phone-alert',
    getCoords(item) {
      if (item.longitude && item.latitude) return [item.longitude, item.latitude]
      var lp = item.lastPosition
      if (lp && lp.longitude && lp.latitude) return [lp.longitude, lp.latitude]
      if (Array.isArray(item.positionsHistory) && item.positionsHistory.length > 0) {
        var last = item.positionsHistory[item.positionsHistory.length - 1]
        if (last && last.longitude && last.latitude) return [last.longitude, last.latitude]
      }
      return null
    },
    getTitle(item) {
      var ud = item.userData || {}
      var name = [ud.name, ud.lastName1, ud.lastName2].filter(Boolean).join(' ').trim()
      return name || item.name || item.callbackNumber || ud.phoneNumber || item.phone || 'SOS'
    },
    getSubtitle(item) {
      var map = {
        CREATED: 'Creada', WAITING_OPERATOR: 'Esperando operador',
        IN_PROGRESS: 'En atención', CLIENT_END_CALL: 'Finalizada por reportante',
        OPERATOR_END_CALL: 'Finalizada por operador', ENDED: 'Finalizada',
      }
      return map[item.status] || item.status || 'SOS'
    },
    getStatus(item) {
      if (item.status === 'CLIENT_END_CALL' || item.status === 'OPERATOR_END_CALL' || item.status === 'ENDED') return 'offline'
      return 'alert'
    },
    getIcon() { return 'sos' },
  },

  route_lpr: {
    entityType: 'route_lpr',
    get color() { return ENTITY_COLORS.route_lpr },
    isStatic: false,
    label: 'Rutas LPR',
    mdiIcon: 'mdi-map-marker-path',
    getCoords(item) {
      if (!item.longitude || !item.latitude) return null
      return [item.longitude, item.latitude]
    },
    getTitle(item) { return item.plate || item.name || 'Ruta LPR ' + (item.id || item._id) },
    getSubtitle(item) { return item.categoryName || '' },
    getStatus() { return 'online' },
    getIcon() { return 'route_lpr' },
  },
}

export function getAllEntityTypes() {
  return Object.keys(ENTITY_CONFIGS)
}

export function getStaticEntityTypes() {
  return Object.entries(ENTITY_CONFIGS)
    .filter(([, cfg]) => cfg.isStatic)
    .map(([key]) => key)
}

export function getDynamicEntityTypes() {
  return Object.entries(ENTITY_CONFIGS)
    .filter(([, cfg]) => !cfg.isStatic)
    .map(([key]) => key)
}

// ¿Esta entidad tiene un estado de CONEXIÓN (en línea / fuera de línea)?
//
// Solo lo tienen las entidades cuyo getStatus puede devolver ambos valores:
// device, bus, camera, lpr y sensor. Las demás devuelven un estado fijo
// ('alert' en reportantes, sismos, incendios y medidas cautelares; 'online'
// en presas, CPR y sensores externos) o uno de otra naturaleza — un incidente
// es 'alert' o 'idle', que habla de si está activo, no de si está conectado.
//
// Importa porque el sidebar contaba `status !== 'online'` como "fuera de
// línea": un incidente ACTIVO se mostraba como desconectado. No es que el
// filtro fuera inútil, es que el dato era falso. Donde esto devuelve false,
// el sidebar oculta el filtro y los contadores en vez de inventarlos.
//
// Una entidad nueva no tiene el flag y por lo tanto NO ofrece el filtro
// (fail-safe): mejor omitir un control que mostrar un conteo que miente.
export function hasConnectionStatus(entityType) {
  const cfg = ENTITY_CONFIGS[entityType]
  return !!(cfg && cfg.hasConnectionStatus)
}
